import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { JobNames } from '../../../framework.core/JobNames';
import MblMilestoneView, { MblMilestoneHandle } from './implementation/MblMilestone';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import { AddEventToNotice, CreateCargoEventLog, FindIndexOfKeyStartsWith, prepareOtherOpForView } from './implementation/mblUtils';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { SQLInfo } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { getSQLInfoList } from '../../../fms.core/data/TrackingModule';
import { MblStatusViewModel, OceanMblStatusMap, OiContainerEvents } from '../../../fms.core/MblStatusViewModel';
import SecondaryButton from '../../../ui/SecondaryButton';
import useSetEditedId from '../../../hooks/useSetEditedId';
import { CargoEventName } from '../../../framework.core/CargoEventName';
import { CargoEventLog } from '../../../fms.core/CargoEventLog';
import constants from '../../../constants';
import { GenMain } from '../../../fms.core/GenMain';
import { GenContainer } from '../../../fms.core/GenContainer';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';
import { formatDateTime } from '../../../dateUtils';

const eventMappings = [
  { prefix: "0|", name: CargoEventName.FmsBooking, seq: 9, mappingKey:'0' },
  { prefix: "1|", name: CargoEventName.EmptyOut, seq: 11, mappingKey:'1' },
  { prefix: "250", name: CargoEventName.FullIn, seq: 13, mappingKey:'250' },
  { prefix: "3|", name: CargoEventName.VesselLoaded, seq: 15, mappingKey:'15' },
  { prefix: "260,470", name: CargoEventName.VesselDeparted, seq: 16, mappingKey:'260' },
  { prefix: "5|", name: CargoEventName.CarrierRls, seq: 17, mappingKey:'5' },
  { prefix: "6|", name: CargoEventName.CustomsRls, seq: 18, mappingKey:'6' },
  { prefix: "270,460", name: CargoEventName.VesselArrived, seq: 19, mappingKey:'270' },
  { prefix: "275", name: CargoEventName.VesselDischarged, seq: 20, mappingKey:'275' },
  { prefix: "9|", name: CargoEventName.FmsDlvrToCfs, seq: 5, mappingKey:'9' },
  { prefix: "290", name: CargoEventName.RailDeparted, seq: 21, mappingKey:'290' },
  { prefix: "295", name: CargoEventName.RailArrived, seq: 22, mappingKey:'295' },
  { prefix: "12|", name: CargoEventName.PickAvail, seq: 23, mappingKey:'12' },
  { prefix: "13|", name: CargoEventName.FullOut, seq: 24, mappingKey:'13' },
  { prefix: "14|", name: CargoEventName.Delivered, seq: 25, mappingKey:'14' },
  { prefix: "15|", name: CargoEventName.EmptyIn, seq: 26, mappingKey:'15' },
];

function getStorageKey(id: number) {
  return `ot-mbl-bl-status-${id}`;
}

function MblMilestoneImpl({
  mblId,
  storageKey,
  onClose,
  isView,
}: {
  mblId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean
}) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const setSavedEntity = useSetEditedId([JobNames.OIContainer]);
  const setSavedEntityOT = useSetEditedId([JobNames.OtherContainer]);

  const [initialized, setInitialized] = useState(false);
  
  const [mbl, setMbl, removeMbl, mblRef] = useTypedFastStorage<GenMain>(storageKey, null);
  const [/*blStatus*/, /*setBlStatus*/, removeBlStatus, /*blStatusRef*/] = useTypedFastStorage<MblStatusViewModel>(getStorageKey(mblId), null);
  const {setCleanState} = useDirtyCheck(
    storageKey,
    ['updated', 'updatedBy']);
    
  useEffect(() => {
    initialize();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose() {
    cleanup();
    onClose();
  }

  function cleanup() {
    removeMbl();
    removeBlStatus();
  }

  async function initialize() {
    const apiResult:ApiResult<GenMain> = await appContext.api.getJobDetail(JobNames.OtherOP, { id: mblId});
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Cannot load data');
      doClose();
      return;
    }
    const loadedMbl =  new GenMain(apiResult.result);

    const blApiResult: ApiResult<MblStatusViewModel> = await appContext.api.getMblStatusViewModel(JobNames.OtherOP, mblId);
    loadedMbl.milestone = new MblStatusViewModel(blApiResult?.result||null);
    prepareOtherOpForView(loadedMbl);

    loadedMbl.milestoneContainerSelectedIndex = loadedMbl.genContainerList && loadedMbl.genContainerList.length > 0
      ? 0
      : -1;

    const loadedModelObj = new GenMain(_.cloneDeep(loadedMbl));
    setCleanState(loadedModelObj);
    setMbl(loadedModelObj);
    if(mblRef.current !== null) {
      mblRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function save() {
    if (!mbl || !mblRef.current) {
      throw new Error('Invalid operation');
    }

    if(!mblRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate("T_GENMAIN", mblRef.current?.id??0, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }

    const blStatusInfoList = getSQLInfoList(mbl as TrackableEntity);
    blStatusInfoList.push(...si);

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveMblStatusViewModel(blStatusInfoList);
    
    if (apiResult && apiResult.success) {
      await UpdateLogMblMilestone(blStatusInfoList, mbl);    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      setSavedEntity(mblRef.current.id.toString(), false);
      setSavedEntityOT(mblRef.current.id.toString(), false, false, true);
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }

    doClose();
  }

  async function UpdateLogMblMilestone(blStatusInfoList: SQLInfo[], mbl:GenMain) {
    const cntrDataWithSeq: {
      loc: string,
      cntrNo: string,
      seq: number,
    }[] = []

    const dicEvent = CollectMblEvents(blStatusInfoList, cntrDataWithSeq);
    if (dicEvent.length > 0) {
      const cmile: CargoEventLog[] = [];
      const lastEvents: CargoEventLog[] = [];

      for (const mapping of eventMappings) {
        const dindex = FindIndexOfKeyStartsWith(dicEvent, mapping.prefix);
        if (dindex >= 0) {
          const cargoEventLog = CreateCargoEventLog(mbl.mblNo, mapping.name, dicEvent[dindex], cntrDataWithSeq.filter(x => x.seq === mapping.seq), appContext.user?.id ?? '');
          cmile.push(...cargoEventLog);
        }
      }

      const uniqueCmileSet = new Set(cmile.map(item => item.cntrNo));
      Array.from(uniqueCmileSet).forEach(x => {
        const tempData = cmile.filter(c => c.cntrNo === x).sort((a, b) => new Date(a.utcTime ?? '').getTime() - new Date(b.utcTime ?? '').getTime());
        lastEvents.push(tempData[tempData.length - 1]);
      });

      if (lastEvents.length > 0) {
        const apiResult = await appContext.api.trackingLogMblEvents(JobNames.OtherOP, mblId, lastEvents);;
        if (!apiResult || !apiResult.success) {
          await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        }
      }
    }
  }

  function CollectMblEvents(blStatusInfoList: SQLInfo[], cntrDataWithSeq: { loc: string, cntrNo: string, seq: number }[]) {
    const changeData: { pimsCntrId: number, seqNum: number, dateTime: string }[] = [];
    const dicEvent: { key: string, value: string }[] = [];

    const changeContainerData = blStatusInfoList.filter(x => x.tableName === 'T_GENCONTAINER');

    OiContainerEvents.forEach((enumEvent) => {
      changeContainerData.forEach(x => {
        const findData = x.fields.find(x => x.propertyName.endsWith(OceanMblStatusMap.get(enumEvent)?.cntrDate ?? ''));
        if (findData) {
          // const pimsCntrId = mbl?.genContainerList.find(y => y.id === x.key?.value)?.pimsLinkId ?? 0;
          changeData.push({ pimsCntrId: 0, seqNum: enumEvent, dateTime: formatDateTime(findData.value) });

          const containerNo = mbl?.genContainerList.find(y => y.id === x.key?.value)?.containerNo ?? ''; //GetLocNameFromEvent
          const cntr = mbl?.genContainerList.find(x => x.containerNo === containerNo);
          const cntrLoc = OceanMblStatusMap.get(enumEvent)?.cntrLoc ?? '';

          cntrDataWithSeq.push({ loc: Object.getOwnPropertyDescriptor(cntr, cntrLoc)?.value, seq: enumEvent, cntrNo: containerNo })
        }
      })
    });
    changeData.forEach(x => {
      const seqNum = x.seqNum;
      const mappingKey = eventMappings.find(x => x.seq === seqNum)?.mappingKey;
      if (mappingKey)
        AddEventToNotice(dicEvent, `${mappingKey}|${x.dateTime}`, String(x.pimsCntrId));
    });

    return dicEvent;
  }

  return (
    <div className='report-popup'>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }

      {!!mbl &&
        <MblMilestoneInitializedImpl mbl={mbl} setMbl={setMbl} isView={isView} save={save} close={doClose} />
      }
    </div>
  );
}

function MblMilestoneInitializedImpl({
  mbl,
  setMbl,
  isView,
  save,
  close
}: {
  mbl: GenMain,
  setMbl: (x:Partial<GenMain>) => void,
  isView: boolean,
  save: () => Promise<void>,
  close: VoidFunction
}) {
  const appContext = useContext(ApplicationContext);
  const milestoneRef = useRef<MblMilestoneHandle>(null);

  const containerInfo:GenContainer[] = mbl.genContainerList ?? [];

  function changeContainerInfo(ci:GenContainer[]) {
    setMbl({ genContainerList: ci });
  }

  async function applyToAll() {
    if (milestoneRef.current) {
      milestoneRef.current.executeApplyToAll();
      await save();
    }
  }

  return (
    <>
      <MblMilestoneView
        ref={milestoneRef}
        mbl={mbl}
        onChange={setMbl}
        isView={isView}
        containerInfo={containerInfo}
        onContainerInfoChange={changeContainerInfo}
        user={appContext.user?.id ?? ''}
      />
      <div className='report-footer'>
        <SecondaryButton
          name="ot-Milestone-apply-to-all-container-button"
          size="regular"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={applyToAll}>
          Apply to All Container
        </SecondaryButton>
        <SecondaryButton
          size="regular"
          name="ot-Milestone-update-button"
          disabled={isView || mbl.milestoneContainerSelectedIndex < 0}
          onClick={save}>
          Update
        </SecondaryButton>
        <SecondaryButton
          name="ot-Milestone-cancel-button"
          size="regular"
          onClick={close}>
          Cancel
        </SecondaryButton>
      </div>
    </>
  );
}

const MblMilestonePopup = ({
  mblId,
  storageKey,
  onClose,
  isView
}:{
  mblId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean,
}) => {
  return (
    <MblMilestoneImpl mblId={mblId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView}/>
  );
};

MblMilestonePopup.defaultProps = {
};

MblMilestonePopup.propTypes = {
  mblId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired
};

export default React.memo(MblMilestonePopup);


